/**
 * 家长之间的共享接口（见方案 4.1「共享」，路径以后端实际注册为准）。
 *
 * 实际路径：
 *  - `GET   /shares/inbox`                         收件箱（别人发给我、待我处理）
 *  - `POST  /shares/:id/accept` · `/reject`         同意 / 拒绝
 *  - `POST  /students/:id/shares`                   发起共享（显式挂在学生下）
 *  - `GET   /students/:id/shares`                   某学生的共享列表
 *  - `PATCH /students/:id/shares/:shareId`          改权限
 *  - `PATCH /students/:id/shares/:shareId/revoke`   取消共享
 *  - `POST  /students/:id/leave`                    接受方取消关联
 *
 * ⚠️ 曾经的 `/shares/students`（"共享给我的学生"聚合端点）**后端并不存在**，
 * 已删除。需要这个列表时，用 `GET /students`（已包含共享给我的学生，带 permission 字段）
 * 在前端组合出来 —— 见 `listSharedStudents` 的说明。
 */
import { api, tryRequest, withFallback } from './client';
import { pickArray } from './shape';
import { toStudentListItem, type RawStudentListItem } from './students';
import type { ShareListResponse, ShareRequest, StudentListItem } from './types';

export type SharePermissionLevel = 'editor' | 'viewer';

export const SHARE_PERMISSION_LABEL: Record<SharePermissionLevel, string> = {
  editor: '可编辑',
  viewer: '仅查看',
};

/**
 * 把后端 `GET /shares/inbox` 的一条原始记录转成前端模型。
 *
 * ⚠️ 字段名必须与后端一致（见 `apps/api/src/modules/shares/shares.service.ts` 的 `listInbox`）：
 *   后端返回 `{ shareId, studentId, studentNickname, invitedById, inviterNickname,
 *               inviterRelationLabel, permission, expiresAt, createdAt }`
 *
 *   这里曾经按"想当然"的名字读（`id` / `counterpartNickname` / `counterpartUsername`），
 *   结果：`id` 取不到 → 下面的 `if (!id) return null` 把**每一条都丢掉** →
 *   收件箱永远为空 → 家长根本看不到别人发来的共享邀请，"同意/拒绝"无从操作。
 *   现在同时接受两套命名（后端为准，旧名保留兼容），并对缺失字段显式降级。
 */
function normalizeShareRequest(raw: unknown, direction: 'incoming' | 'outgoing'): ShareRequest | null {
  if (raw === null || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;

  const str = (key: string): string => (typeof record[key] === 'string' ? (record[key] as string) : '');

  // 后端用 shareId；兼容旧名 id
  const id = str('shareId') || str('id');
  if (!id) return null;

  return {
    id,
    studentId: str('studentId'),
    studentNickname: str('studentNickname'),
    // 后端把"对方"叫 inviterNickname（收件箱里对方就是邀请人）。
    // 后端**不提供**对方用户名（只有昵称与身份标签），因此这里可能为空，
    // 界面上不要假设它一定有值。
    counterpartNickname: str('inviterNickname') || str('counterpartNickname'),
    counterpartUsername: str('counterpartUsername'),
    permission: record.permission === 'viewer' ? 'viewer' : 'editor',
    status:
      typeof record.status === 'string' ? (record.status as ShareRequest['status']) : 'pending',
    direction: record.direction === 'outgoing' ? 'outgoing' : direction,
    message: typeof record.message === 'string' ? record.message : null,
    createdAt: str('createdAt'),
  };
}

/**
 * 待我处理的共享邀请。
 *
 * ⚠️ 后端 `GET /shares/inbox` 返回的是 **`{ invitations: [...] }`** —— 只有一个键，
 *   而且内容**全部是"别人发给我、待我处理"的邀请**（`toParentId = 我` 且 `status = 'pending'`）。
 *   **后端没有"我发出的邀请"这个列表。**
 *
 *   早期这里按 `incoming` / `items` / `shares` 去找数组，一个都不匹配 →
 *   `invitations` 被忽略 → 收件箱永远为空（即使真有邀请）。
 *
 * 因此：`incoming` 用 `invitations` 取值；`outgoing` 在拿到后端支持前**恒为空**，
 * 页面需要如实说明，不要让它看起来像"真的没有我发出的邀请"。
 */
export async function listShares(): Promise<ShareListResponse> {
  return withFallback(
    async () => {
      const data = await api.get<unknown>('/shares/inbox');
      const incoming = pickArray<unknown>(data, 'invitations', 'incoming', 'items', 'shares')
        .map((item) => normalizeShareRequest(item, 'incoming'))
        .filter((item): item is ShareRequest => item !== null);
      const outgoing = pickArray<unknown>(data, 'outgoing', 'sent')
        .map((item) => normalizeShareRequest(item, 'outgoing'))
        .filter((item): item is ShareRequest => item !== null);
      return { incoming, outgoing };
    },
    { incoming: [], outgoing: [] } as ShareListResponse,
  );
}

/**
 * "共享给我的学生"。
 *
 * ⚠️ 这里刻意**不再**调用 `/shares/students`（后端没有这个聚合端点）。
 * 改为先用 `GET /students`（它已经区分"我创建的"与"共享给我"，每条带 `permission`），
 * 再把非 owner 的那些筛出来。这样只发一个请求，而且与家长首页的数据同源，
 * 不会出现"首页说共享给我 2 个、共享页说 3 个"这种对不上的情况。
 */
export async function listSharedStudents(): Promise<StudentListItem[]> {
  const result = await tryRequest(() => api.get<unknown>('/students'));
  if (result.state === 'notReady') return [];
  // 形状规整（含余额的显式解析）复用 students.ts 的同一个函数：
  // 这样"我创建的"与"共享给我的"两条路径拿到的卡片数据一定同源。
  return pickArray<unknown>(result.data, 'students', 'items')
    .filter(
      (item): item is RawStudentListItem =>
        item !== null &&
        typeof item === 'object' &&
        typeof (item as { id?: unknown }).id === 'string' &&
        (item as { permission?: unknown }).permission !== 'owner',
    )
    .map(toStudentListItem);
}

export async function createShare(payload: {
  studentId: string;
  targetUsername?: string;
  targetEmail?: string;
  permission: SharePermissionLevel;
}): Promise<void> {
  const { studentId, ...body } = payload;
  await api.post(`/students/${studentId}/shares`, body);
}

export async function acceptShare(shareId: string): Promise<void> {
  await api.post(`/shares/${shareId}/accept`);
}

export async function rejectShare(shareId: string): Promise<void> {
  await api.post(`/shares/${shareId}/reject`);
}

/**
 * 改权限。后端是嵌套路径，必须带上 studentId
 * （显式表达"这是某个学生的共享"，也避免 shareId 跨学生串号）。
 */
export async function updateSharePermission(
  studentId: string,
  shareId: string,
  permission: SharePermissionLevel,
): Promise<void> {
  await api.patch(`/students/${studentId}/shares/${shareId}`, { permission });
}

/**
 * 取消共享。后端用 `PATCH .../revoke` 而不是 `DELETE`
 * —— 共享记录要留痕（方案 2.4：revoked 状态保留历史数据）。
 */
export async function cancelShare(studentId: string, shareId: string): Promise<void> {
  await api.patch(`/students/${studentId}/shares/${shareId}/revoke`, {});
}

/** 接受方取消关联（不再能看到该学生）。 */
export async function leaveStudent(studentId: string): Promise<void> {
  await api.post(`/students/${studentId}/leave`);
}
